import { VendorModel, VendorStoreModel } from '@models/VendorModel';
import { CouponModel } from './CouponModel';
import { ScratchModel } from './ScratchModel';
import { CustomerModel } from './CustomerModel';
import { Country, LoyaltyPlanQuestionType, RewardType } from './Enums';

export class LoyaltyPlanModel {

    public constructor(init?: Partial<Pick<LoyaltyPlanModel, keyof LoyaltyPlanModel>>) {

        if (init) {
            if (typeof init.startDateAt === 'string') {
                init.startDateAt = new Date(init.startDateAt);
            }
            if (typeof init.endDateAt === 'string') {
                init.endDateAt = new Date(init.endDateAt);
            }
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updatedAt === 'string') {
                init.updatedAt = new Date(init.updatedAt);
            }
            if (typeof init.deletedAt === 'string') {
                init.deletedAt = new Date(init.deletedAt);
            }
            if (init.coupons && init.coupons.length) {
                init.coupons = init.coupons.map((item: any) => new LoyaltyPlanStickerCouponModel(item));
            }
            if (init.scratches && init.scratches.length) {
                init.scratches = init.scratches.map((item: any) => new LoyaltyPlanStickerScratchModel(item));
            }
            if (init.customers && init.customers.length) {
                init.customers = init.customers
                    .sort((a: any, b: any) => {
                        // Ordena poniendo primero los que tienen finishedAt == null
                        if (a.finishedAt === null && b.finishedAt !== null) return -1;
                        if (a.finishedAt !== null && b.finishedAt === null) return 1;
                        return 0;
                    })
                    .map((item: any) => new LoyaltyPlanCustomerStickerModel(item));
            }
            if (init.vendor) {
                init.vendor = new VendorModel(init.vendor);
            }
            if (init.vendors && init.vendors.length) {
                init.vendors = init.vendors.map((item: any) => new VendorModel(item));
            }

            init.planExpiresInDays = Number(init.planExpiresInDays || 0);

            Object.assign(this, init);
        }
    }

    id: number;
    vendorId: number;

    country = Country.CR;

    name: string;
    description: string;
    terms: string;

    iconUrl: string;
    colorIcon: string;
    colorTheme: string;
    logoUrl: string;

    stickersQuantity: number;
    assignStickersQuantity: number;

    startDateAt: Date;

    // Vigencia por fecha límite: vence ese día y es igual para todos. null si el plan no usa este modo.
    endDateAt?: Date;

    // Vigencia por días: vence N días después de que CADA cliente se afilió. 0 si el plan no usa este modo.
    planExpiresInDays = 0;

    isPublic = false;
    showInApp = false;

    createdAt: Date;
    updatedAt?: Date;
    deletedAt?: Date;
    disabledAt?: Date;

    vendor: VendorModel;

    coupons: LoyaltyPlanStickerCouponModel[] = [];
    scratches: LoyaltyPlanStickerScratchModel[] = [];
    customers: LoyaltyPlanCustomerStickerModel[] = [];
    vendors: VendorModel[] = [];


    redeemedQuantity: number = 0;
    purchasesCount: number = 0;

    get customer(): LoyaltyPlanCustomerStickerModel {

        return this.customers[0] ? this.customers[0] : null;
    }

    get hasWonPrizes(): boolean {

        return this.customer?.stickers.some(s => s.couponId > 0);
    }

    get notClaimedStickers(): LoyaltyPlanCustomerStickerModel[] {

        return this.customer?.stickers?.filter(s => s.claimedAt == null);
    }

    get ordinalList(): IOrdinalList[] {

        const list: IOrdinalList[] = [];

        for (let i = 0; i < Number(this.stickersQuantity); i++) {
            const ordinal = i + 1;

            const coupon = this.coupons.find(c => c.ordinal === ordinal);
            const scratch = this.scratches.find(s => s.ordinal === ordinal);

            list.push({
                ordinal: ordinal,
                couponId: coupon ? coupon.couponId : 0,
                scratchId: scratch ? scratch.scratchId : 0,
                isActive: coupon ? coupon?.coupon?.isActive : scratch?.isActive,
                isClaimed: true,
            });


            //set the not claimed stickers
            if(this.notClaimedStickers?.find(x => x.ordinal == ordinal) && 
            (coupon?.couponId > 0 || scratch?.scratchId > 0) && (coupon?.coupon?.isActive || scratch?.isActive)) {
                list[list.length - 1].isClaimed = false;
            }

        }

        return list;
    }
}

export class LoyaltyPlanCustomerStickerModel {

    public constructor(init?: Partial<Pick<LoyaltyPlanCustomerStickerModel, keyof LoyaltyPlanCustomerStickerModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updatedAt === 'string') {
                init.updatedAt = new Date(init.updatedAt);
            }
            if (typeof init.deletedAt === 'string') {
                init.deletedAt = new Date(init.deletedAt);
            }
            if (typeof init.finishedAt === 'string') {
                init.finishedAt = new Date(init.finishedAt);
            }
            if (init.loyaltyPlan) {
                init.loyaltyPlan = new LoyaltyPlanModel(init.loyaltyPlan);
            }
            if (init.scratch) {
                init.scratch = new ScratchModel(init.scratch);
            }
            if (init.coupon) {
                init.coupon = new CouponModel(init.coupon);
            }
            if (init.store) {
                init.store = new VendorStoreModel(init.store);
            }
            if (init.stickers && init.stickers.length) {
                init.stickers = init.stickers.map((item: any) => new LoyaltyPlanCustomerStickerModel(item));
            }
            Object.assign(this, init);
        }
    }

    id: number;
    loyaltyPlanId: number;
    customerId: number;

    createdAt: Date;
    updatedAt: Date;
    deletedAt?: Date;
    finishedAt?: Date;
    unsubscribeAt?: Date;
    claimedAt?: Date;
    ordinal: number;

    couponId: number;
    scratchId: number;

    loyaltyPlan?: LoyaltyPlanModel;
    customer: CustomerModel;
    stickers: LoyaltyPlanCustomerStickerModel[] = [];

    scratch: ScratchModel;
    coupon: CouponModel;
    store: VendorStoreModel;
}


export interface IOrdinalList {

    ordinal: number;
    couponId: number;
    scratchId: number;
    isActive?: boolean;
    isClaimed?: boolean;
}

export interface IManageLoyaltyItem {

    ordinal?: number;
    loyaltyPlan: LoyaltyPlanModel;
    couponId?: number;
    scratchId?: number;
}

export class LoyaltyPlanStickerCouponModel {

    public constructor(init?: Partial<Pick<LoyaltyPlanStickerCouponModel, keyof LoyaltyPlanStickerCouponModel>>) {
        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updatedAt === 'string') {
                init.updatedAt = new Date(init.updatedAt);
            }
            if (typeof init.deletedAt === 'string') {
                init.deletedAt = new Date(init.deletedAt);
            }
            if (init.coupon) {
                init.coupon = new CouponModel(init.coupon);
            }
            Object.assign(this, init);
        }
    }

    id: number;
    loyaltyPlanId: number;
    couponId: number;
    ordinal: number;
    isActive = false;
    createdAt: Date;
    updatedAt?: Date;
    deletedAt?: Date;

    coupon: CouponModel;
}

export class LoyaltyPlanStickerScratchModel {

    public constructor(init?: Partial<Pick<LoyaltyPlanStickerScratchModel, keyof LoyaltyPlanStickerScratchModel>>) {
        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updatedAt === 'string') {
                init.updatedAt = new Date(init.updatedAt);
            }
            if (typeof init.deletedAt === 'string') {
                init.deletedAt = new Date(init.deletedAt);
            }
            if (init.scratch) {
                init.scratch = new ScratchModel(init.scratch);
            }
            Object.assign(this, init);
        }
    }

    id: number;
    loyaltyPlanId: number;
    scratchId: number;
    ordinal: number;
    createdAt: Date;
    updatedAt?: Date;
    deletedAt?: Date;
    isActive = false;

    scratch: ScratchModel;
}

export class JoinLoyaltyPlanModel {

    public constructor(init?: Partial<Pick<JoinLoyaltyPlanModel, keyof JoinLoyaltyPlanModel>>) {
        if (init) {

            Object.assign(this, init);
        }
    }

    loyaltyPlanId: number;
    loyaltyPlanLinkId?: number;
    customerId: number;
    
    answers: LoyaltyPlanCustomerFormAnswerModel[] = [];
}

// QR masivo leido desde la web (sin app)
export class AssignmentCodeWebInfoModel {

    public constructor(init?: Partial<Pick<AssignmentCodeWebInfoModel, keyof AssignmentCodeWebInfoModel>>) {
        if (init) {

            Object.assign(this, init);
        }
    }

    assignmentCode: string;

    // Plan del bloque del QR
    qrLoyaltyPlanId?: number;

    // Plan al que se afilia quien no esta en el plan (tier mas bajo o el del QR) y su link principal
    joinLoyaltyPlanId?: number;
    joinLoyaltyPlanLinkId?: number;
}

export class ClaimAssignmentCodeModel {

    public constructor(init?: Partial<Pick<ClaimAssignmentCodeModel, keyof ClaimAssignmentCodeModel>>) {
        if (init) {

            Object.assign(this, init);
        }
    }

    assignmentCode: string;
    customerId: number;

    // Respuestas del formulario del plan al que se afilia (solo si no estaba afiliado)
    answers: LoyaltyPlanCustomerFormAnswerModel[] = [];
}

export class ClaimAssignmentCodeResultModel {

    // Plan y afiliacion donde cayo el sello
    loyaltyPlanId: number;
    loyaltyPlanCustomerId: number;
    loyaltyPlanName: string;
    stickersQuantity: number;
    stickersCount: number;

    // true si se afilio en este reclamo; joinResult trae los premios de bienvenida
    joined: boolean;
    joinResult: any;

    // Sello asignado (ordinal para marcarlo en la tarjeta)
    sticker: { id: number, ordinal: number };

    // Tarjeta del plan donde cayo el sello
    plan: ClaimPlanCardModel;

    // El sello completo el plan y paso a newPlan (siguiente tier, o el mismo plan si es el ultimo / sin tier)
    planCompleted: boolean;
    newPlan: ClaimPlanCardModel;
    newPlanWelcomeGifts: WelcomeGiftModel[] = [];

    // Premio (cupon) o raspadita del sello del QR
    prize: ClaimPrizeModel;
    scratch: ClaimScratchModel;
}

export class ClaimPlanCardModel {
    loyaltyPlanId: number;
    loyaltyPlanCustomerId: number;
    vendorId: number;
    name: string;
    colorTheme: string;
    colorIcon: string;
    iconUrl: string;
    stickersQuantity: number;
    stickersCount: number;

    // QR del plan para sumar sellos en el comercio (el mismo de la app)
    planQrData: string;
}

export class ClaimPrizeModel {
    transactionId: number;
    name: string;
    description: string;
    imageUrl: string;

    // QR del cupon (el mismo del correo)
    qrData: string;
    appleWalletUrl: string;
    googleWalletUrl: string;
}

export class ClaimScratchModel {
    scratchId: number;
    name: string;
    logoUrl: string;

    // Ruta para jugarla (la misma del correo)
    playPath: string;
}

export class TutorialRewardModel {

    public constructor(init?: Partial<Pick<TutorialRewardModel, keyof TutorialRewardModel>>) {
        if (init) {

            Object.assign(this, init);
        }
    }

    type: RewardType;
    id: number;
    name: string;
    description: string;
    imageUrl: string;
}

// Premio de bienvenida (cupón) que el usuario recibe automáticamente al unirse al plan.
// Viene en el response de PostJoinLoyaltyPlan dentro de la lista welcomeGifts (puede ser null o vacía).
export class WelcomeGiftModel {

    public constructor(init?: Partial<Pick<WelcomeGiftModel, keyof WelcomeGiftModel>>) {
        if (init) {

            Object.assign(this, init);
        }
    }

    transactionId: number;
    name: string;
    description: string;
    imageUrl?: string;
    appleWalletUrl?: string;
    googleWalletUrl?: string;

    // QR del cupon (el mismo del correo)
    qrData?: string;
}

export class UnsubscribeLoyaltyPlanModel {

    public constructor(init?: Partial<Pick<UnsubscribeLoyaltyPlanModel, keyof UnsubscribeLoyaltyPlanModel>>) {
        if (init) {

            Object.assign(this, init);
        }
    }

    loyaltyPlanId: number;
    customerId: number;
}

export class CheckAutoAssignCodeModel {

    public constructor(init?: Partial<Pick<CheckAutoAssignCodeModel, keyof CheckAutoAssignCodeModel>>) {
        if (init) {

            Object.assign(this, init);
        }
    }

    loyaltyPlanId: number;
    customerId: number;
    assignmentCode: string;

    // Plan y afiliacion donde cae el sello (si esta en otro tier del comercio, su tier vigente)
    targetLoyaltyPlanId?: number;
    targetLoyaltyPlanCustomerId?: number;
}

export class AssignCustomerStickerModel {

    public constructor(init?: Partial<Pick<AssignCustomerStickerModel, keyof AssignCustomerStickerModel>>) {
        if (init) {

            Object.assign(this, init);
        }
    }

    loyaltyPlanId: number;

    customerId: number;

    comments: string;
    assignmentCode?: string;

    vendorId: number;
    storeId: number;
    vendorCustomerId: number;
    loyaltyPlanCustomerId: number;
}

export class GetLoyaltyPlanCustomerModel {

    public constructor(init?: Partial<Pick<GetLoyaltyPlanCustomerModel, keyof GetLoyaltyPlanCustomerModel>>) {
        if (init) {

            Object.assign(this, init);
        }
    }

    loyaltyPlanId: number;
    customerId: number;
}

export class LoyaltyPlanLinkModel {

    constructor(init?: Partial<LoyaltyPlanLinkModel>) {
        if (init) {
            Object.assign(this, init);
            if (init.questions && init.questions.length) {
                init.questions = init.questions.map((item: any) => new LoyaltyPlanFormQuestionModel(item));
            }
        }
    }

    id: number;
    loyaltyPlanId: number;

    name: string;
    description: string;
    link: string;

    imageUrl: string;
    isMainForm: boolean = false;
    isActive: boolean = false;

    createdAt: Date;
    updatedAt: Date;
    deletedAt?: Date;

    customersCount: number = 0;

    questions: LoyaltyPlanFormQuestionModel[] = [];

    loyaltyPlan: LoyaltyPlanModel;
}

export class LoyaltyPlanFormQuestionModel {

    public constructor(init?: Partial<Pick<LoyaltyPlanFormQuestionModel, keyof LoyaltyPlanFormQuestionModel>>) {
        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updateAt === 'string') {
                init.updateAt = new Date(init.updateAt);
            }
            if (typeof init.deletedAt === 'string') {
                init.deletedAt = new Date(init.deletedAt);
            }
            if (init.questionsAnswers && init.questionsAnswers.length) {
                init.questionsAnswers = init.questionsAnswers.map((item: any) => new LoyaltyPlanFormQuestionAnswerModel(item));
            }
            Object.assign(this, init);
        }
    }

    id: number = 0;
    loyaltyPlanId: number;
    loyaltyPlanLinkId: number;
    question: string;
    type: LoyaltyPlanQuestionType;
    ordinal: number;
    createdAt?: Date;
    updateAt?: Date;
    deletedAt?: Date;
    questionsAnswers: LoyaltyPlanFormQuestionAnswerModel[] = [];

    get questionActive(): boolean{

        return this.deletedAt == null? true: false;
    }

    get isSelectQuestion(): boolean {

        return this.type === LoyaltyPlanQuestionType.SelectAnswer;
    }
}

export class LoyaltyPlanFormQuestionAnswerModel {

    public constructor(init?: Partial<Pick<LoyaltyPlanFormQuestionAnswerModel, keyof LoyaltyPlanFormQuestionAnswerModel>>) {
        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updateAt === 'string') {
                init.updateAt = new Date(init.updateAt);
            }
            if (typeof init.deletedAt === 'string') {
                init.deletedAt = new Date(init.deletedAt);
            }
            Object.assign(this, init);
        }
    }

    id: number = 0;
    loyaltyPlanFormQuestionId: number;
    answer: string;
    ordinal: number;
    createdAt?: Date;
    updateAt?: Date;
    deletedAt?: Date;

    get answerActive(): boolean{

        return this.deletedAt == null? true: false;
    }
}

export class LoyaltyPlanCustomerFormAnswerModel {

    public constructor(init?: Partial<Pick<LoyaltyPlanCustomerFormAnswerModel, keyof LoyaltyPlanCustomerFormAnswerModel>>) {
        if (init) {
        if (typeof init.createdAt === 'string') {
            init.createdAt = new Date(init.createdAt);
        }
        if (typeof init.updateAt === 'string') {
            init.updateAt = new Date(init.updateAt);
        }
        if (typeof init.deletedAt === 'string') {
            init.deletedAt = new Date(init.deletedAt);
        }
        Object.assign(this, init);
        }
    }

    id: number = 0;
    loyaltyPlanId: number;
    customerId: number;
    loyaltyPlanLinkId?: number;
    loyaltyPlanFormQuestionId?: number;
    loyaltyPlanFormQuestionAnswerId?: number;
    answer: string;
    createdAt?: Date;
    updateAt?: Date;
    deletedAt?: Date;
}

export class LoyaltyPlanCustomerModel {
    public constructor(init?: Partial<Pick<LoyaltyPlanCustomerModel, keyof LoyaltyPlanCustomerModel>>) {

        if (init) {
            if (typeof init.createdAt === 'string') {
                init.createdAt = new Date(init.createdAt);
            }
            if (typeof init.updatedAt === 'string') {
                init.updatedAt = new Date(init.updatedAt);
            }
            if (typeof init.deletedAt === 'string') {
                init.deletedAt = new Date(init.deletedAt);
            }
            if (typeof init.affiliatedAt === 'string') {
                init.affiliatedAt = new Date(init.affiliatedAt);
            }
            if (typeof init.finishedAt === 'string') {
                init.finishedAt = new Date(init.finishedAt);
            }
            if (init.loyaltyPlan) {
                init.loyaltyPlan = new LoyaltyPlanModel(init.loyaltyPlan);
            }
            if (init.customer) {
                init.customer = new CustomerModel(init.customer);
            }
            if (init.stickers && init.stickers.length) {
                init.stickers = init.stickers.map((item: any) => new LoyaltyPlanCustomerStickerModel(item));
            }
            Object.assign(this, init);
        }
    }

    id: number;

    loyaltyPlanId: number;

    customerId: number;

    loyaltyPlanLinkId: number;

    // Fecha de cuando se afilio al plan
    affiliatedAt: Date;

    linkName: string

    // Fecha cuando completo el plan
    finishedAt: Date;

    createdAt: Date;
    updatedAt: Date;
    deletedAt: Date;

    loyaltyPlan: LoyaltyPlanModel;
    redeemedQuantity: number;

    customer: CustomerModel
    stickers: LoyaltyPlanCustomerStickerModel[] = [];

    form: LoyaltyPlanLinkModel;
    answers: LoyaltyPlanCustomerFormAnswerModel[] = [];

    get lastSticker(): LoyaltyPlanCustomerStickerModel {

        return this.stickers[this.stickers.length - 1];
    }

    get earnedStickers(): number {

        return this.stickers.filter(x => x.couponId).length;
    }

    
}